/**
 * Learn Tips — on/off toggle for the "?" help-badge overlay (W20 learn-tip
 * sweep, 2026-10-02). Mirrors `themeStore.ts`'s pattern (Zustand via
 * `createAppStore`, manual `localStorage` persistence, no backend round
 * trip) rather than the portal's server-persisted `$learnMode` nanostore —
 * the Social SPA has no per-user preferences API, and a client-only toggle
 * is enough for a help affordance that's off by default.
 *
 * Default is OFF, matching the portal's own Learn-Mode default: the badges
 * are a discoverability aid a user opts into, not a constant visual layer
 * across ~190 anchors app-wide.
 */

import { createAppStore } from "@plerity/dashboard-shared/stores";

const STORAGE_KEY = "social-learn-tips-enabled";

interface LearnTipsState {
  enabled: boolean;
  toggle: () => void;
  setEnabled: (enabled: boolean) => void;
}

function getInitialEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persist(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // localStorage unavailable (private mode, quota) — in-memory state still works.
  }
}

export const useLearnTipsStore = createAppStore<LearnTipsState>("social-learn-tips", (set) => ({
  enabled: getInitialEnabled(),
  toggle: () =>
    set((s) => {
      const next = !s.enabled;
      persist(next);
      return { enabled: next };
    }),
  setEnabled: (enabled) => {
    persist(enabled);
    set({ enabled });
  },
}));
